/**
  * The user object.
  * @deprecated Use shared/models instead.
  */
export class User {
  /**
    *  The id of the client that the user belongs to.
    */
  clientId: string;

  /**
    *  The email that the user registered with.
    */
  email: string;

  /**
    *  The id of the user; auto-generated by AWS Cognito.
    */
  userId: string;

  /**
    *  The role of the user.
    */
  role: "unknown" | "admin" | "superAdmin";

  /**
    *  The type of the user.
    */
  type: "customer" | "client" | "cwc";

  /**
    *  If the user has a subscription.
    */
  hasSubscription?: boolean;

  /**
    *  The non-searchable data of the user.
    */
  data: {

    /**
      *  The registered credit cards of the user.
      */
    ccInfo?: any,

    /**
      *  The loyalty programs that the user has points in.
      */
    loyalty?: {
      /**
        *  The loyalty program that the user has points in.
        */
      [loyaltyProgramId: string]: number
    },

    /**
      *  The subscriptions that the user has purchased.
      */
    subscriptions?: {
      /**
        *  A subscription that the user has purchased.
        */
      [subscriptionId: string]: UserSubscription
    },

    /**
      *  Future data.
      */
    [key: string]: any

  };

  /**
    *  If the user has logged into the app.
    */
   newUser?: boolean;

  /**
    *  When the user was last updated.
    */
  timeStamp: number;

  /**
    *  The user's first name.
    */
  firstName?: string;

  /**
    *  The user's last name.
    */
  lastName?: string;

  /**
    *  When the user was last updated.
    */
  modifiedOn?: number;

  /**
    *  When the user last accessed their app or administration panel.
    */
  accessedOn?: number;

  /**
    *  When the user was created. This will be missing on older users.
    */
  createdOn?: number;

  /**
    *  The devices that have access the user's account.
    */
  devices?: { [deviceVendorId: string]: UserDevice };

  /**
    *  The most recent active device on the user's account.
    */
  currentDevice?: string;

  /**
    *  The flags that have been activated on the user's account.
    */
  flags?: UserFlag[];

  /**
    *  The current status of the user, enable/disable
    */
  status?: string;

  /**
    *  Info on the last status change
    */
  statusLog?: statusLog;
}

export interface statusLog {
  /**
    *  User ID of the admin that made the status change
    */
  changedByAdminId: string;

  /**
    *  The system that made the status change
    */
  changedBySystem: string;

  /**
    *  Date that the status was changed on
    */
  changedOn: number;

  /**
    *  Reason for the status change
    */
  changeReason: string;
}

export interface UserSubscription {

  /**
    *  If the user has cancelled their subscription.
    */
  cancelled?: boolean;

  /**
    *  The currency that the user purchased the subscription with.
    */
  currency: string;

  /**
    *  The codes that the user has requested in the current subscription cycle.
    */
  history: UserSubscriptionCode[];

  /**
    *  The location that the subscription was purchased at.
    */
  locationId: string;

  /**
    *  The most recent successfully processed subscription or renewal transaction id.
    */
  oldTransactionId?: string;

  /**
    *  The record of the base price of the subscription when the user purchased the subscription.
    */
  price: number;

  /**
    *  If the subscription has been processed by the payment processor.
    */
  processed: boolean;

  /**
    *  The product id for the product that the subscription will provide codes for.
    */
  productId: string;

  /**
    *  When the subscription was last renewed.
    */
  renewed: number;

  /**
    *  When the subscription will renew.
    */
  renews: number;

  /**
    *  The sale id of the subscription that was purchased.
    */
  saleId: string;

  /**
    *  The location sharing configuration for the subscription.
    */
  sharing: {

    /**
      *  The sharing group id.
      */
    groupId?: string;

    /**
      *  The list of locations that the user can request codes from.
      */
    snapShot: string[];
  }

  /**
    *  The auto-generated subscription id for the subscription.
    */
  subscriptionId: string;

  /**
    *  When the subscription was added.
    */
  subscriptionStartedOn?: number;

  /**
    *  The id of the subscription transaction that initialized the subscription.
    */
  subscriptionTransactionId?: string;

  /**
    *  The record of the tax that the user pays on the subscription.
    */
  tax: number;

  /**
    *  The transaction id of the upcoming subscription of renewal transaction to be processed.
    */
  transactionId: string;

  /**
    *  The usage limits of the subscription.
    */
  usageLimits: {

    /**
      *  The type of geo-location restrition applied to the subscription.
      */
    geoLimitMethod: string;

    /**
      *  If the user can request an unlimited number of codes in the subscription cycle.
      */
    unlimited: boolean;

    /**
      *  The restriction for the number of codes a user can request in a given day.
      */
    day: UserSubscriptionUsageLimit;

    /**
      *  The restriction for the number of codes a user can request in a given week.
      */
    week: UserSubscriptionUsageLimit

    /**
      *  The restriction for the number of codes a user can request in a given month.
      */
    month: UserSubscriptionUsageLimit
  }
}

/**
  *  The code restriction object.
  */
export interface UserSubscriptionUsageLimit {
  /**
    *  The number of codes that can be requested during the given restriction. Will be a negative number if the restriction does not apply.
    */
  qty: number;

  /**
    *  If the given restriction does not apply.
    */
  unlimited: boolean;

}

/**
  *  Subscription code object.
  */
export interface UserSubscriptionCode {

  /**
    *  The client id of the client that the code was provided from.
    */
  clientId: string;

  /**
    *  The wash code to be used at the carwash.
    */
  code: number;

  /**
    *  The display version of the wash code. This is used to prevent preceding 0's from being removed from the wash code.
    */
  codeStr?: string;

  /**
    *  How to display the code.
    */
  displayAs: "digits" | "controller" | "attendant" | "none";

  /**
    *  When the code expires. Limited to one hour from when the code was requested.
    */
  expiry: number;

  /**
    *  List of locations that the code can be used at.
    */
  locations: string[];

  /**
    *  The id of the product that the code will provide.
    */
  productId: string;

  /**
    *  The id of the sale that the code was provided by.
    */
  saleId: string;

  /**
    *  When the code was provided.
    */
  timeStamp: number;

  /**
    *  The id of the transaction that the code is associated with.
    */
  transactionId: string;

  /**
    *  If the code has been used.
    */
  used?: boolean;

  /**
    *  When the code was used.
    */
  usedOn?: number;

  /**
    *  The id of the location where the code was used.
    */
  usedAt?: string;
}

/**
  *  User device object.
  */
export interface UserDevice {
  /**
    *  The model of the device.
    */

  device: string;
  /**
    *  The operating system the device is running.
    */
  osType: string;

  /**
    *  The operating system the device is running.
    */
  appVersion: string;

  /**
    *  The version of the operating system the device is running.
    */
  osVersion: string;

  /**
    *  The unique identifier of the device.
    */
  vendor: string;

  /**
    *  If the device has a cellular internet connection.
    */
  cellularConnection: string;

  /**
    *  The timestamp of when the device was first activated.
    */
  createdOn: number;

  /**
    *  The timestamp of the most recent time the device was active.
    */
  accessedOn: number;

}

/**
  *  User flag object.
  */
export interface UserFlag {

  /**
    *  The event id that triggered the flag.
    */
  id: string

  /**
    *  The name of the flag.
    */
  name: string;

  /**
    *  The timestamp of when the flag was set.
    */
  createdOn: number;

  /**
    *  The description of the flag.
    */
  description: string;

  /**
    *  The id of the device that caused the event.
    */
  deviceId: string;

}
